import { ref, watch } from "vue"
import { api } from "@/lib/api"
import { useAuth, user } from "@/composables/auth/useAuth"
import type { ProfileData } from "@/types/settings"
import { useMessage } from "naive-ui"
import { debug, error as logError } from "@/utils/debug"

// Shared state - created once and reused across all components
const profileData = ref<ProfileData | null>(null)
const loading = ref(false)
const saving = ref(false)
const loaded = ref(false)
let fetchPromise: Promise<{ data: ProfileData | null; error: any }> | null = null
let initPromise: Promise<void> | null = null

/**
 * Initialize profile data when user is authenticated
 * This is a singleton - only runs once per user session
 */
async function initialize() {
  // Return existing promise if already initializing
  if (initPromise) {
    debug("👤 initialize: Already initializing, returning existing promise")
    return initPromise
  }

  // Don't initialize if no user
  if (!user.value?.id) {
    debug("👤 initialize: No user, skipping")
    return
  }

  debug("👤 initialize: Starting initialization", new Error().stack)

  // Create and store the initialization promise
  initPromise = (async () => {
    debug("👤 Initializing profile module...")
    await fetchProfileInternal()
    loaded.value = true
    debug("👤 Profile loaded successfully")
  })()

  return initPromise
}

/**
 * Internal fetch function - does the actual API call
 */
async function fetchProfileInternal() {
  // If already fetching, return the existing promise
  if (fetchPromise) {
    debug("👤 fetchProfileInternal: Already fetching, returning existing promise")
    return fetchPromise
  }

  debug("👤 fetchProfileInternal: Starting new API call", new Error().stack)
  loading.value = true

  fetchPromise = (async () => {
    try {
      const { data, error } = await api<ProfileData>("fetchProfile", {})

      if (error) {
        // BUGFIX: Don't show toast for authentication errors (expected on login page)
        // Only show toast for actual API failures
        if (error.code !== "UNAUTHENTICATED") {
          logError("Failed to load profile:", error)
        }
        return { data: null, error }
      }

      profileData.value = data || null
      return { data, error: null }
    } catch (error) {
      logError("Error fetching profile:", error)
      return { data: null, error }
    } finally {
      loading.value = false
      fetchPromise = null // Clear the promise so future calls can fetch again
    }
  })()

  return fetchPromise
}

// Watch for auth state changes (ONLY for logout/login transitions, not initial load)
watch(user, (newUser, oldUser) => {
  if (oldUser && !newUser) {
    // User logged out - clear profile data
    debug("👤 User logged out, clearing profile")
    profileData.value = null
    loaded.value = false
    initPromise = null
  } else if (oldUser && newUser && oldUser.id !== newUser.id) {
    // User switched accounts - reinitialize with new user
    debug("👤 User switched accounts, reinitializing profile")
    profileData.value = null
    loaded.value = false
    initPromise = null
    initialize()
  }
  // Note: We don't handle !oldUser && newUser here because that's initial load
  // and auto-initialization in useProfile() already handles it
})

let callCount = 0
export function useProfile() {
  const { user: currentUser, updateUser } = useAuth()

  callCount++
  debug(`👤 useProfile: Called (count: ${callCount})`, new Error().stack)

  // Auto-initialize on first use if user is authenticated
  const shouldInit = currentUser.value?.id && !initPromise && !loaded.value
  if (shouldInit) {
    debug(
      `👤 useProfile: Auto-initializing (initPromise: ${!!initPromise}, loaded: ${loaded.value})`,
      new Error().stack,
    )
    initialize()
  } else {
    debug(
      `👤 useProfile: Skipping init (hasUser: ${!!currentUser.value?.id}, hasInitPromise: ${!!initPromise}, loaded: ${loaded.value})`,
    )
  }

  /**
   * Fetch the current user's profile data
   * @param force - Force refetch even if we have cached data
   */
  async function fetchProfile(force = false) {
    debug(
      `👤 fetchProfile: Called (force: ${force}, loaded: ${loaded.value}, hasData: ${!!profileData.value})`,
      new Error().stack,
    )

    // If we already have data and not forcing refetch, return cached data
    if (profileData.value && !force) {
      debug("👤 fetchProfile: Returning cached data (has profileData)")
      return { data: profileData.value, error: null }
    }

    // If already initialized and not forcing, return cached data
    if (loaded.value && !force) {
      debug("👤 fetchProfile: Returning cached data (loaded)")
      return { data: profileData.value, error: null }
    }

    // Otherwise fetch fresh data
    debug("👤 fetchProfile: Calling fetchProfileInternal")
    return fetchProfileInternal()
  }

  /**
   * Update profile data
   */
  async function updateProfile(updates: Partial<ProfileData>) {
    const message = useMessage()
    saving.value = true
    try {
      // Update profile data via API
      const { error: profileError } = await api("updateProfile", updates)

      if (profileError) {
        message.error(profileError.message || "Failed to update profile")
        return { success: false, error: profileError }
      }

      // Update user metadata in Supabase auth if username or country changed
      if (updates.username || updates.country || updates.first_name || updates.last_name) {
        const { error: authError } = await updateUser({
          data: {
            username: updates.username,
            country: updates.country,
            first_name: updates.first_name,
            last_name: updates.last_name,
          },
        })

        if (authError) {
          message.error(authError.message || "Failed to update auth metadata")
          return { success: false, error: authError }
        }
      }

      // Refetch profile data from server to ensure we have the latest state
      // This is more reliable than optimistic updates, especially for nullable fields
      await fetchProfileInternal()

      message.success("Profile updated successfully")
      return { success: true, error: null }
    } catch (error) {
      logError("Error updating profile:", error)
      message.error("An error occurred while updating profile")
      return { success: false, error }
    } finally {
      saving.value = false
    }
  }

  /**
   * Update profile picture
   */
  async function updateProfilePicture(newUrl: string) {
    return updateProfile({ profile_pic_url: newUrl })
  }

  /**
   * Update cover image
   */
  async function updateCoverImage(newUrl: string) {
    return updateProfile({ cover_pic_url: newUrl })
  }

  return {
    user,
    profileData,
    loading,
    loaded,
    saving,
    fetchProfile,
    updateProfile,
    updateProfilePicture,
    updateCoverImage,
  }
}
